import { GraphQLClient, RequestDocument, Variables } from 'graphql-request';
import { GRAPHQL_ENDPOINT } from '../config/api';
import { isGraphQLError } from './errorUtils';

/**
 * Type for Auth0's getAccessTokenSilently function
 */
export type GetAccessTokenSilently = (options?: {
  authorizationParams?: { audience?: string };
}) => Promise<string>;

/**
 * Creates an authenticated GraphQL client using Auth0 token.
 *
 * @param getAccessTokenSilently - Auth0 function to get access token
 * @returns Configured GraphQLClient with authorization header
 */
export const createAuthenticatedClient = async (
  getAccessTokenSilently: GetAccessTokenSilently,
): Promise<GraphQLClient> => {
  const token = await getAccessTokenSilently({
    authorizationParams: {
      audience: 'santtai-api',
    },
  });

  return new GraphQLClient(GRAPHQL_ENDPOINT, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Creates an unauthenticated GraphQL client.
 * Use this for public endpoints that don't require authentication.
 *
 * @returns Configured GraphQLClient without authorization header
 */
export const createUnauthenticatedClient = (): GraphQLClient => {
  return new GraphQLClient(GRAPHQL_ENDPOINT);
};

/**
 * Standardized GraphQL request function with optional authentication.
 * This is the recommended way to make GraphQL requests in hooks.
 *
 * @param document - GraphQL query/mutation document
 * @param variables - Variables for the query/mutation
 * @param getAccessTokenSilently - Optional Auth0 function for authenticated requests
 * @returns Promise resolving to the query/mutation result
 */
export const graphqlRequest = async <
  TData = unknown,
  TVariables extends Variables = Variables,
>(
  document: RequestDocument,
  variables?: TVariables,
  getAccessTokenSilently?: GetAccessTokenSilently,
): Promise<TData> => {
  let client: GraphQLClient;

  if (getAccessTokenSilently) {
    // Authenticated request
    client = await createAuthenticatedClient(getAccessTokenSilently);
  } else {
    // Unauthenticated request - use unauthenticated client for consistency
    client = createUnauthenticatedClient();
  }

  // Use client.request for both cases to avoid type issues
  if (variables !== undefined && variables !== null) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return client.request<TData, any>(document, variables as any);
  }
  return client.request<TData>(document);
};

/**
 * Determines if a query should be retried based on error type.
 * This function is used for React Query retry logic.
 *
 * @param failureCount - Current number of failures
 * @param error - The error that occurred
 * @returns true if query should retry, false otherwise
 */
export const shouldRetryQuery = (
  failureCount: number,
  error: unknown,
): boolean => {
  // Don't retry after 3 attempts
  if (failureCount >= 3) {
    return false;
  }

  // Don't retry on authorization errors
  if (isGraphQLError(error)) {
    const hasAuthError = error.response?.errors?.some(
      e => e?.extensions?.code === 'AUTH_NOT_AUTHORIZED',
    );
    if (hasAuthError) {
      return false;
    }
  }

  // Retry for other errors
  return true;
};
